import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, Beaker, FileText, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    axios.get(`${API}/products`).then(res => {
      setAllProducts(res.data);
      const found = res.data.find(p => p.slug === slug);
      setProduct(found || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  const scrollToRFQ = () => {
    window.location.href = '/#rfq';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#0A7C84] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-24 text-center">
          <h1 className="text-2xl text-[#0A1128] mb-4" style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}>Product Not Found</h1>
          <Link to="/products" className="text-sm text-[#0A7C84] hover:underline" style={{ fontFamily: 'var(--font-body)' }}>
            Back to All Products
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const currentIndex = allProducts.findIndex(p => p.slug === slug);
  const prevProduct = currentIndex > 0 ? allProducts[currentIndex - 1] : null;
  const nextProduct = currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null;

  const seoTitle = `${product.name} | ${product.cas_number} | Biolyn Pharmaceutical B.V.`;
  const seoDesc = `${product.name} (CAS: ${product.cas_number}) — ${product.purity} purity. ${product.tagline}. European B2B supplier. Request a quote from Biolyn Pharmaceutical B.V.`;

  return (
    <div data-testid="product-detail-page" className="min-h-screen bg-white">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://biolynpharma.com/products/${product.slug}`} />
        {product.image_url && <meta property="og:image" content={product.image_url} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDesc} />
        <link rel="canonical" href={`https://biolynpharma.com/products/${product.slug}`} />
      </Helmet>

      <Navbar />

      <div className="pt-24 pb-8 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-[#A0AAB2] mb-8" style={{ fontFamily: 'var(--font-mono)' }} data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#0A1128] transition-colors duration-150">Home</Link>
          <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
          <Link to="/products" className="hover:text-[#0A1128] transition-colors duration-150">Products</Link>
          <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
          <span className="text-[#0A1128]">{product.name}</span>
        </nav>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left - Image */}
          <div className="lg:col-span-5">
            <div className="border border-[#DEE2E6] bg-[#F8F9FA] aspect-square flex items-center justify-center sticky top-24">
              {product.image_url ? (
                <img
                  src={product.image_url}
                  alt={`${product.name} pharmaceutical vial`}
                  className="w-full h-full object-cover"
                  data-testid="product-detail-image"
                />
              ) : (
                <Beaker className="w-16 h-16 text-[#DEE2E6]" strokeWidth={1} />
              )}
            </div>
          </div>

          {/* Right - Details */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <Badge
                variant="outline"
                className="text-[#0A7C84] border-[#0A7C84]/20 rounded-none text-xs"
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                {product.status === 'active' ? 'In Stock' : 'Inquiry'}
              </Badge>
              <span className="text-xs text-[#A0AAB2]" style={{ fontFamily: 'var(--font-mono)' }}>
                CAS: {product.cas_number}
              </span>
            </div>

            <h1
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A1128] mb-2"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 900 }}
              data-testid="product-detail-name"
            >
              {product.name}
            </h1>

            <p
              className="text-base md:text-lg text-[#6C757D] mb-6"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              {product.tagline}
            </p>

            <p
              className="text-sm text-[#495057] leading-relaxed mb-8"
              style={{ fontFamily: 'var(--font-body)' }}
              data-testid="product-detail-description"
            >
              {product.description}
            </p>

            {product.technical_description && (
              <div className="border-t border-[#DEE2E6] pt-6 mb-8">
                <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-2" style={{ fontFamily: 'var(--font-mono)' }}>
                  Technical Profile
                </span>
                <p className="text-sm text-[#495057] leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                  {product.technical_description}
                </p>
              </div>
            )}

            {/* Technical Specs Grid */}
            <div className="border border-[#DEE2E6] mb-8" data-testid="product-detail-specs">
              <div className="grid grid-cols-2 md:grid-cols-3">
                {[
                  { label: 'CAS Number', value: product.cas_number },
                  { label: 'Molecular Weight', value: product.molecular_weight },
                  { label: 'Molecular Formula', value: product.molecular_formula },
                  { label: 'Purity', value: product.purity },
                  { label: 'Form', value: product.form },
                  { label: 'Storage', value: product.storage },
                ].map((spec, idx) => (
                  <div
                    key={spec.label}
                    className={`p-4 ${idx % 3 !== 2 ? 'md:border-r border-[#DEE2E6]' : ''} ${idx % 2 !== 1 ? 'border-r md:border-r-0 border-[#DEE2E6]' : ''} ${idx < 4 ? 'border-b border-[#DEE2E6]' : ''} ${idx < 3 ? 'md:border-b border-[#DEE2E6]' : ''}`}
                  >
                    <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-0.5" style={{ fontFamily: 'var(--font-mono)' }}>
                      {spec.label}
                    </span>
                    <span className="text-sm text-[#0A1128]" style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>
                      {spec.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Applications */}
            {product.applications?.length > 0 && (
              <div className="mb-8">
                <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-3" style={{ fontFamily: 'var(--font-mono)' }}>
                  Applications
                </span>
                <div className="flex flex-wrap gap-2">
                  {product.applications.map((app, i) => (
                    <span
                      key={i}
                      className="px-3 py-1.5 text-xs bg-[#F8F9FA] border border-[#DEE2E6] text-[#495057]"
                      style={{ fontFamily: 'var(--font-body)' }}
                    >
                      {app}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTAs */}
            <div className="flex gap-3 pt-6 border-t border-[#DEE2E6]">
              <button
                onClick={scrollToRFQ}
                className="btn-pill group flex items-center gap-2 px-6 py-3 bg-[#0A7C84] text-white text-sm hover:bg-[#075E64] transition-colors duration-150"
                style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
                data-testid="product-detail-rfq"
              >
                Request a Quote
                <ArrowRight className="w-4 h-4 transition-transform duration-150 group-hover:translate-x-0.5" strokeWidth={1.5} />
              </button>
              <button
                onClick={scrollToRFQ}
                className="btn-pill flex items-center gap-2 px-6 py-3 border border-[#DEE2E6] text-[#495057] text-sm hover:bg-[#F8F9FA] transition-colors duration-150"
                style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
                data-testid="product-detail-coa"
              >
                <FileText className="w-4 h-4" strokeWidth={1.5} />
                Request CoA
              </button>
            </div>
          </div>
        </div>

        {/* Prev/Next Navigation */}
        <div className="flex items-center justify-between border-t border-[#DEE2E6] mt-16 pt-8">
          {prevProduct ? (
            <Link
              to={`/products/${prevProduct.slug}`}
              className="group flex items-center gap-2 text-sm text-[#495057] hover:text-[#0A1128] transition-colors duration-150"
              style={{ fontFamily: 'var(--font-body)' }}
              data-testid="product-prev"
            >
              <ArrowLeft className="w-4 h-4 transition-transform duration-150 group-hover:-translate-x-0.5" strokeWidth={1.5} />
              {prevProduct.name}
            </Link>
          ) : <div />}
          {nextProduct ? (
            <Link
              to={`/products/${nextProduct.slug}`}
              className="group flex items-center gap-2 text-sm text-[#495057] hover:text-[#0A1128] transition-colors duration-150"
              style={{ fontFamily: 'var(--font-body)' }}
              data-testid="product-next"
            >
              {nextProduct.name}
              <ArrowRight className="w-4 h-4 transition-transform duration-150 group-hover:translate-x-0.5" strokeWidth={1.5} />
            </Link>
          ) : <div />}
        </div>
      </div>

      <Footer />
    </div>
  );
}
